import { useEffect, useMemo, useState } from 'react'
import { Download, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import ExcelImportModal from '../../components/ExcelImportModal'
import FilterBar from '../../components/FilterBar'
import FormRow from '../../components/FormRow'
import RowActions from '../../components/RowActions'
import StatusBadge from '../../components/StatusBadge'
import TextField from '../../components/TextField'
import { SwitchField } from '../../components/Form'
import { excel as EXCEL } from '../../components/strings'
import { useToast } from '../../components/toastContext'
import { phishingSettingsStrings as S } from './strings'
import { serverText } from '../../lib/i18n'

/**
 * One small managed list — the phishing categories, or the landing links.
 *
 * The two tabs are the same transaction with different columns: a server-paged table, a
 * two-or-three field drawer, and a delete. They are one component rather than two so the
 * table, the drawer and the four states cannot drift apart between the tabs; what differs is
 * passed in.
 *
 * @param {{key: string, label: string, dir?: string}[]} fields  what the drawer edits
 * @param {boolean} [withStatus]  whether the row carries an active flag
 * @param {string} [stateKey]  WHICH list this is — the name its position is remembered
 *   under. One component serves two tabs, so without it they would share one position and
 *   restore each other's; omit it and nothing is remembered.
 * @param {{onExport: Function, onImport: Function, note: string}} [transfer]  export/import
 */
export default function PhishingListPanel({
  columns,
  fields,
  labels,
  withStatus = false,
  stateKey,
  list,
  save,
  remove,
  transfer,
}) {
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  /* WHERE THIS TAB WAS LEFT. The panel unmounts on every tab switch, so the search, the
     page and the page size were all discarded by moving to الإعدادات and back — page 1 of
     an unsearched list every time. Remembered under this list's own name, since the
     categories and the links are two lists sharing one component. */
  const [query, setQuery] = useRemembered(stateKey && `${stateKey}:query`, '')
  const [page, setPage] = useRemembered(stateKey && `${stateKey}:page`, 1)
  const [pageSize, setPageSize] = useRemembered(stateKey && `${stateKey}:pageSize`, 25)
  const [reloadKey, setReloadKey] = useState(0)
  const [editing, setEditing] = useState(null)
  const [values, setValues] = useState({})
  const [saving, setSaving] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [importing, setImporting] = useState(false)

  /* A new search is a new list, so it starts at its first page — on a CHANGE only, or the
     remembered page would be thrown away the moment it was restored. The table cannot do
     this itself: the list is SERVER-paged, so the page belongs to this panel. */
  useOnChange(() => setPage(1), [query])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    list({ page, pageSize, query })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setTotal(response.total)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [list, page, pageSize, query, reloadKey, toast])

  const open = (row) => {
    const blank = Object.fromEntries(fields.map((field) => [field.key, '']))
    setValues(row ? { ...blank, ...row } : { ...blank, isActive: true })
    setEditing(row ?? {})
  }

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  const submit = async () => {
    setSaving(true)
    const outcome = await save(values)
    setSaving(false)
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || S.saved)
    setEditing(null)
    setReloadKey((key) => key + 1)
    return undefined
  }

  const confirmDelete = async () => {
    const row = pendingDelete
    setPendingDelete(null)
    const outcome = await remove(row.id)
    /* The categories delete REFUSES when a template still uses the category, and says so —
       that message is the whole point of showing the server's own text here. */
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || S.deleted)
    setReloadKey((key) => key + 1)
    return undefined
  }

  const runImport = async (file) => {
    setImporting(false)
    const outcome = await transfer.onImport(file)
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || labels.imported)
    setReloadKey((key) => key + 1)
    return undefined
  }

  const runExport = async () => {
    const outcome = await transfer.onExport()
    if (!outcome.ok) toast.error(outcome.message || S.actionFailed)
  }

  const allColumns = useMemo(
    () => [
      ...columns,
      ...(withStatus
        ? [
            {
              key: 'isActive',
              header: S.categories.status,
              value: (row) => (row.isActive ? S.active : S.inactive),
              render: (row) => (
                <StatusBadge tone={row.isActive ? 'success' : 'dark'}>
                  {row.isActive ? S.active : S.inactive}
                </StatusBadge>
              ),
            },
          ]
        : []),
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              { id: 'edit', icon: Pencil, label: S.edit, onClick: () => open(row) },
              {
                id: 'delete',
                icon: Trash2,
                label: S.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    // `open` closes over `fields`, which is stable per tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, withStatus],
  )

  return (
    <>
      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: columns[0]?.header }}
        actions={
          <>
            {transfer && (
              <>
                <Button variant="soft" onClick={() => setImporting(true)}>
                  <Upload size={16} strokeWidth={1.75} />
                  {S.import}
                </Button>
                <Button variant="soft" onClick={runExport}>
                  <Download size={16} strokeWidth={1.75} />
                  {S.export}
                </Button>
              </>
            )}
            <Button onClick={() => open(null)}>
              <Plus size={16} strokeWidth={2} />
              {labels.add}
            </Button>
          </>
        }
      />

      <DataTable
        columns={allColumns}
        rows={rows}
        loading={loading}
        query={query}
        getRowId={(row) => row.id}
        emptyMessage={labels.empty}
        server={{
          page,
          pageSize,
          total,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
          },
        }}
      />

      <Drawer
      dialog
        open={!!editing}
        title={editing?.id ? labels.edit : labels.add}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button onClick={submit} disabled={saving || !String(values[fields[0].key] ?? '').trim()}>
              {S.save}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              {S.close}
            </Button>
          </>
        }
      >
        {fields.map((field) => (
          <FormRow key={field.key} label={field.label}>
            {(id) => (
              <TextField
                id={id}
                dir={field.dir}
                type={field.type}
                value={values[field.key] ?? ''}
                onChange={set(field.key)}
              />
            )}
          </FormRow>
        ))}
        {withStatus && (
          <SwitchField
            checked={values.isActive !== false}
            onChange={set('isActive')}
            label={S.categoryForm.enabled}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={!!pendingDelete}
        message={labels.deleteConfirm}
        confirmLabel={S.delete}
        cancelLabel={S.close}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {transfer && (
        <ExcelImportModal
          open={importing}
          title={S.import}
          accept=".xls,.xlsx"
          strings={{ intro: EXCEL.intro, notes: transfer.note }}
          onClose={() => setImporting(false)}
          onSubmit={runImport}
        />
      )}
    </>
  )
}
